/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * Aggiornamento: Linea Guida AIB n.5 CFRS Sicilia (2022-2024) - Linea 5B
 * Nuovi campi: ModelloDrone CFRS, AbilitazioneENAC, MissioneDrone,
 *              autonomiaResidua, quotaMetri, coordsRilevate, oraRilevamento,
 *              telefonoOperatore, condivisioneRealtime
 * Nuova funzione: pulsante "📍 GPS Drone → COAU" per trasferimento automatico
 *                 delle coordinate rilevate dal drone al form COAU
 */
// src/components/Risorse/DroniTab.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { SelectModal } from '../common/SelectModal';
import { MicInput, MicTextarea, MicTimeInput } from '../common/MicInput';
import type { ModelloDrone, AbilitazioneENAC, MissioneDrone } from '../../types';

// ================================================================
// Modelli drone secondo dotazione CFRS (Linea 5B)
// ================================================================
const MODELLI_DRONE_OPTIONS = [
  'DJI Mini 2',                  // torrettisti CFRS, no ENAC, <249g
  'DJI Mavic 2 Enterprise Advanced', // PG/PS CFRS, termocamera IR
  'DJI Mavic 3',
  'DJI Inspire',
  'DJI M210',
  'SenseFly',
  'Sky Eye Systems',
  'Altro',
];

// Mappa da testo visualizzato a tipo interno
const MODELLO_DISPLAY_TO_TIPO: Record<string, ModelloDrone> = {
  'DJI Mini 2': 'DJI_MINI_2',
  'DJI Mavic 2 Enterprise Advanced': 'MAVIC_ENTERPRISE_ADV',
  'DJI Mavic 3': 'DJI_MAVIC_3',
  'DJI Inspire': 'DJI_INSPIRE',
  'SenseFly': 'SENSFLY',
  'Sky Eye Systems': 'SKY_EYE',
};

const POSIZIONI_OPERATIVE = [
  'Disponibile',
  'In Volo',
  'In Ricognizione',
  'In Monitoraggio',
  'Sul Fronte',
  'In Attesa',
  'Rientro Base',
  'Manutenzione',
];

const MISSIONI: { valore: MissioneDrone; label: string }[] = [
  { valore: 'AVVISTAMENTO',   label: '🔍 Avvistamento incendio' },
  { valore: 'MONITORAGGIO',   label: '👁️ Monitoraggio evolutivo' },
  { valore: 'PERIMETRAZIONE', label: '📐 Perimetrazione area (post-evento)' },
  { valore: 'DETERRENZA',     label: '🚁 Pattugliamento/Deterrenza' },
  { valore: 'ISPEZIONE',      label: '🔎 Ispezione zone inaccessibili' },
];

const ABILITAZIONI_ENAC: { valore: AbilitazioneENAC; label: string }[] = [
  { valore: 'NON_RICHIESTA', label: 'Non richiesta (Mini 2 < 249g, volo a vista)' },
  { valore: 'A1_A3',         label: 'ENAC A1-A3 (lontano da centri abitati)' },
  { valore: 'A2',            label: 'ENAC A2 (centri abitati)' },
];

// Helper: quando si sceglie DJI Mini 2, l'abilitazione è automaticamente non richiesta
const getDefaultENAC = (modelloTipo?: ModelloDrone): AbilitazioneENAC =>
  modelloTipo === 'DJI_MINI_2' ? 'NON_RICHIESTA' : 'A1_A3';

// Colori badge missione
const MISSIONE_COLORS: Record<MissioneDrone, string> = {
  AVVISTAMENTO:   '#ef4444',
  MONITORAGGIO:   '#f97316',
  PERIMETRAZIONE: '#8b5cf6',
  DETERRENZA:     '#3b82f6',
  ISPEZIONE:      '#22c55e',
};

// ================================================================

export const DroniTab: React.FC = () => {
  const droni = useAppStore((state) => state.droni);
  const addDrone = useAppStore((state) => state.addDrone);
  const updateDrone = useAppStore((state) => state.updateDrone);
  const deleteDrone = useAppStore((state) => state.deleteDrone);
  const inviaCoordsDroneACOAU = useAppStore((state) => state.inviaCoordsDroneACOAU);

  const emptyForm = {
    modello: '',
    modelloTipo: undefined as ModelloDrone | undefined,
    operatore: '',
    telefonoOperatore: '',
    abilitazioneENAC: undefined as AbilitazioneENAC | undefined,
    autonomiaMinuti: undefined as number | undefined,
    autonomiaResidua: undefined as number | undefined,
    quotaMetri: undefined as number | undefined,
    raggioKm: undefined as number | undefined,
    missione: undefined as MissioneDrone | undefined,
    condivisioneRealtime: false,
    urlStreaming: '',
    coordsRilevate: '',
    oraRilevamento: '',
    posizioneOperativa: '',
    note: '',
  };

  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModelloModal, setShowModelloModal] = useState(false);
  const [showPosizioneModal, setShowPosizioneModal] = useState(false);
  const [showMissioneModal, setShowMissioneModal] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  const showFeedback = (msg: string) => {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  // GPS tablet come fallback — precisione ±5-15m
  const handleGpsTablet = () => {
    if (!navigator.geolocation) {
      alert('⚠️ GPS non disponibile su questo dispositivo');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lon = pos.coords.longitude.toFixed(6);
        const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
        setFormData((p) => ({
          ...p,
          coordsRilevate: `${lat}, ${lon}`,
          oraRilevamento: ora,
        }));
        setGpsLoading(false);
        showFeedback(`⚠️ GPS tablet: ${lat}, ${lon} — precisione approssimativa`);
      },
      (err) => {
        setGpsLoading(false);
        alert('⚠️ Impossibile ottenere la posizione: ' + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleModelloSelect = (value: string) => {
    const tipo = MODELLO_DISPLAY_TO_TIPO[value] ?? 'ALTRO';
    setFormData((prev) => ({
      ...prev,
      modello: value,
      modelloTipo: tipo as ModelloDrone,
      // Auto-compila ENAC per Mini 2
      abilitazioneENAC: getDefaultENAC(tipo as ModelloDrone),
      // Auto-compila autonomia per i modelli CFRS noti
      autonomiaMinuti:
        tipo === 'DJI_MINI_2' ? 31 :
        tipo === 'MAVIC_ENTERPRISE_ADV' ? 31 :
        prev.autonomiaMinuti,
    }));
  };

  const handleNumberChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? undefined : parseInt(value),
    }));
  };

  const handleSubmit = () => {
    if (!formData.modello || !formData.operatore) {
      alert('⚠️ Modello e Operatore sono obbligatori');
      return;
    }
    if (editingId) {
      updateDrone(editingId, formData as any);
      setEditingId(null);
      showFeedback('✅ Drone aggiornato');
    } else {
      addDrone(formData as any);
      showFeedback('✅ Drone salvato');
    }
    setFormData({ ...emptyForm });
  };

  const handleEdit = (drone: any) => {
    setFormData({
      modello: drone.modello || '',
      modelloTipo: drone.modelloTipo,
      operatore: drone.operatore || '',
      telefonoOperatore: drone.telefonoOperatore || '',
      abilitazioneENAC: drone.abilitazioneENAC,
      autonomiaMinuti: drone.autonomiaMinuti,
      autonomiaResidua: drone.autonomiaResidua,
      quotaMetri: drone.quotaMetri,
      raggioKm: drone.raggioKm,
      missione: drone.missione,
      condivisioneRealtime: drone.condivisioneRealtime || false,
      urlStreaming: (drone as any).urlStreaming || '',
      coordsRilevate: drone.coordsRilevate || '',
      oraRilevamento: drone.oraRilevamento || '',
      posizioneOperativa: drone.posizioneOperativa || '',
      note: drone.note || '',
    });
    setEditingId(drone.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (confirm('Eliminare questo drone?')) deleteDrone(id);
  };

  const handleInviaACOAU = (drone: any) => {
    if (!drone.coordsRilevate) {
      alert('⚠️ Nessuna coordinata rilevata per questo drone.\nInserire le coordinate nel campo "GPS Rilevato" prima di inviare al COAU.');
      return;
    }
    inviaCoordsDroneACOAU(drone.id);
    showFeedback(`✅ Coordinate ${drone.coordsRilevate} inviate al COAU`);
  };

  const missioneLabel = (m?: MissioneDrone) =>
    MISSIONI.find((x) => x.valore === m)?.label ?? '';

  const enacLabel = (e?: AbilitazioneENAC) =>
    ABILITAZIONI_ENAC.find((x) => x.valore === e)?.label ?? '';

  return (
    <div className="risorse-tab-content">
      <h3>🛸 Gestione Droni / APR (Linea 5B)</h3>

      {feedbackMsg && (
        <div className="feedback-banner" style={{
          background: '#166534', color: '#fff', padding: '8px 14px',
          borderRadius: '6px', marginBottom: '12px', fontWeight: 600,
        }}>
          {feedbackMsg}
        </div>
      )}

      {/* ===== FORM ===== */}
      <div className="form-container">
        <h4>{editingId ? '✏️ Modifica Drone' : '➕ Nuovo Drone'}</h4>

        {/* Modello + Operatore */}
        <div className="form-row">
          <div className="form-group">
            <label>Modello *</label>
            <input
              type="text"
              value={formData.modello}
              placeholder="Seleziona modello…"
              readOnly
              onClick={() => setShowModelloModal(true)}
              style={{ cursor: 'pointer' }}
            />
            {formData.modelloTipo === 'DJI_MINI_2' && (
              <small style={{ color: '#22c55e' }}>
                ✅ Torrettisti CFRS · &lt;249g · No ENAC obbligatorio
              </small>
            )}
            {formData.modelloTipo === 'MAVIC_ENTERPRISE_ADV' && (
              <small style={{ color: '#f97316' }}>
                🔴 Uso PG/PS · Termocamera IR · ENAC A1-A3 richiesto
              </small>
            )}
          </div>
          <div className="form-group">
            <label>Operatore *</label>
            <MicInput
              type="text"
              placeholder="Nome operatore (digita o ditta vocalmente)"
              value={formData.operatore}
              appendMode={false}
              onChange={(e) => setFormData((p) => ({ ...p, operatore: e.target.value }))}
            />
          </div>
        </div>

        {/* Telefono + ENAC */}
        <div className="form-row">
          <div className="form-group">
            <label>Telefono Operatore</label>
            <input
              type="tel"
              placeholder="es. 335 1234567"
              value={formData.telefonoOperatore}
              onChange={(e) => setFormData((p) => ({ ...p, telefonoOperatore: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Abilitazione ENAC</label>
            <select
              value={formData.abilitazioneENAC ?? ''}
              onChange={(e) => setFormData((p) => ({ ...p, abilitazioneENAC: e.target.value as AbilitazioneENAC }))}
            >
              <option value="">— Seleziona —</option>
              {ABILITAZIONI_ENAC.map((a) => (
                <option key={a.valore} value={a.valore}>{a.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Missione — SelectModal come gli altri menu */}
        <div className="form-group">
          <label>Missione Operativa (Linea 5B)</label>
          <input
            type="text"
            value={formData.missione ? missioneLabel(formData.missione) : ''}
            placeholder="Seleziona missione…"
            readOnly
            onClick={() => setShowMissioneModal(true)}
            style={{ cursor: 'pointer' }}
          />
        </div>

        {/* Parametri di volo */}
        <div className="form-row">
          <div className="form-group">
            <label>Autonomia totale (min)</label>
            <MicInput
              type="text"
              inputMode="numeric"
              appendMode={false}
              placeholder="es. 31"
              value={formData.autonomiaMinuti !== undefined ? String(formData.autonomiaMinuti) : ''}
              onChange={(e) => {
                const n = parseInt(e.target.value.replace(/[^\d]/g, ''));
                setFormData(p => ({ ...p, autonomiaMinuti: isNaN(n) ? undefined : n }));
              }}
            />
          </div>
          <div className="form-group">
            <label>Autonomia residua (min)</label>
            <MicInput
              type="text"
              inputMode="numeric"
              appendMode={false}
              placeholder="min rimanenti"
              value={formData.autonomiaResidua !== undefined ? String(formData.autonomiaResidua) : ''}
              onChange={(e) => {
                const n = parseInt(e.target.value.replace(/[^\d]/g, ''));
                setFormData(p => ({ ...p, autonomiaResidua: isNaN(n) ? undefined : n }));
              }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Quota operativa (m)</label>
            <MicInput
              type="text"
              inputMode="numeric"
              appendMode={false}
              placeholder="es. 120"
              value={formData.quotaMetri !== undefined ? String(formData.quotaMetri) : ''}
              onChange={(e) => {
                const n = parseInt(e.target.value.replace(/[^\d]/g, ''));
                setFormData(p => ({ ...p, quotaMetri: isNaN(n) ? undefined : n }));
              }}
            />
            {formData.abilitazioneENAC === 'NON_RICHIESTA' && (
              <small style={{ color: '#f97316' }}>⚠️ Max 120m senza ENAC</small>
            )}
          </div>
          <div className="form-group">
            <label>Raggio d'azione (km)</label>
            <MicInput
              type="text"
              inputMode="decimal"
              appendMode={false}
              placeholder="es. 0.35"
              value={formData.raggioKm !== undefined ? String(formData.raggioKm) : ''}
              onChange={(e) => {
                const n = parseFloat(e.target.value.replace(/[^.\d]/g, ''));
                setFormData(p => ({ ...p, raggioKm: isNaN(n) ? undefined : n }));
              }}
            />
            {formData.abilitazioneENAC === 'NON_RICHIESTA' && (
              <small style={{ color: '#f97316' }}>⚠️ Max ~0.35 km a vista</small>
            )}
          </div>
        </div>

        {/* Coordinate punto fuoco + Ora + Streaming — allineati in row */}
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div className="form-group">
            <label>📍 GPS Rilevato (lat, lon)</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="es. 37.502669, 14.052149"
                value={formData.coordsRilevate}
                onChange={(e) => setFormData((p) => ({ ...p, coordsRilevate: e.target.value }))}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                title="Usa GPS di questo tablet come fallback (precisione ±5-15m)"
                onClick={handleGpsTablet}
                disabled={gpsLoading}
                style={{
                  background: '#1e3a5f',
                  color: '#93c5fd',
                  border: '1px solid #2563eb',
                  borderRadius: '5px',
                  padding: '6px 10px',
                  cursor: gpsLoading ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {gpsLoading ? '⏳' : '📱 GPS tablet'}
              </button>
            </div>
            <small style={{ color: '#94a3b8' }}>
              Preferibile: coordinate dall'app DJI Fly sul tablet del pilota · 
              <span style={{ color: '#f97316' }}> GPS tablet solo come fallback (±5-15m)</span>
            </small>
          </div>
          <div className="form-group">
            <label>Ora rilevamento</label>
            <MicTimeInput
              value={formData.oraRilevamento}
              onChange={(e) => setFormData((p) => ({ ...p, oraRilevamento: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ minWidth: '200px' }}>
            <label style={{ visibility: 'hidden' }}>_</label>
            <label
              htmlFor="condivisioneRT"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                cursor: 'pointer', padding: '8px 10px',
                border: formData.condivisioneRealtime ? '2px solid #22c55e' : '2px solid #334155',
                borderRadius: '6px',
                background: formData.condivisioneRealtime ? '#14532d' : 'transparent',
                color: formData.condivisioneRealtime ? '#86efac' : '#94a3b8',
                fontWeight: formData.condivisioneRealtime ? 700 : 400,
                transition: 'all 0.2s',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                id="condivisioneRT"
                checked={formData.condivisioneRealtime}
                onChange={(e) => setFormData((p) => ({ ...p, condivisioneRealtime: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: '#22c55e' }}
              />
              📡 Streaming SIM a SOUP/DOS
            </label>
          </div>
        </div>

        {/* URL Streaming — visibile solo se condivisioneRealtime è attivo */}
        {formData.condivisioneRealtime && (
          <div className="form-group">
            <label>🔗 URL Streaming video</label>
            <input
              type="url"
              placeholder="es. https://youtu.be/xxxxx  oppure  192.168.1.x:8080"
              value={formData.urlStreaming}
              onChange={(e) => setFormData((p) => ({ ...p, urlStreaming: e.target.value }))}
            />
            <small style={{ color: '#94a3b8' }}>
              Link generato dall'app DJI, YouTube Live, server WebRTC o IP locale
            </small>
          </div>
        )}

        {/* Posizione operativa */}
        <div className="form-group">
          <label>Posizione Operativa</label>
          <input
            type="text"
            value={formData.posizioneOperativa}
            placeholder="Seleziona posizione…"
            readOnly
            onClick={() => setShowPosizioneModal(true)}
            style={{ cursor: 'pointer' }}
          />
        </div>

        {/* Note */}
        <div className="form-group">
          <label>Note</label>
          <MicTextarea
            rows={2}
            value={formData.note}
            onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))}
          />
        </div>

        <div className="form-actions">
          <button className="btn btn-success" onClick={handleSubmit}>
            {editingId ? '✏️ Salva Modifiche' : '💾 Salva Drone'}
          </button>
          {editingId && (
            <button className="btn btn-secondary" onClick={() => { setFormData({ ...emptyForm }); setEditingId(null); }}>
              ✕ Annulla
            </button>
          )}
        </div>
      </div>

      {/* ===== LISTA ===== */}
      <div className="lista-header">
        <h4>Lista Droni ({droni.length})</h4>
      </div>

      <div className="risorse-list">
        {droni.length === 0 ? (
          <div className="empty-state">
            <p>🛸 Nessun drone inserito</p>
          </div>
        ) : (
          droni.map((drone) => (
            <div key={drone.id} className="risorsa-card">
              <div className="card-header-risorsa">
                <span className="icon">🛸</span>
                <h4>
                  {drone.modello}
                  {drone.missione && (
                    <span
                      className="badge"
                      style={{
                        marginLeft: '8px',
                        background: MISSIONE_COLORS[drone.missione as MissioneDrone] ?? '#64748b',
                        color: '#fff',
                        fontSize: '0.7rem',
                        padding: '2px 7px',
                        borderRadius: '4px',
                      }}
                    >
                      {drone.missione}
                    </span>
                  )}
                  <span className={`badge status-${drone.posizioneOperativa?.toLowerCase().replace(/\s/g, '_')}`}>
                    {drone.posizioneOperativa}
                  </span>
                </h4>
              </div>

              <div className="card-body-risorsa">
                <p><strong>Operatore:</strong> {drone.operatore}
                  {(drone as any).telefonoOperatore && ` · 📞 ${(drone as any).telefonoOperatore}`}
                </p>

                {(drone as any).abilitazioneENAC && (
                  <p><strong>ENAC:</strong> {enacLabel((drone as any).abilitazioneENAC)}</p>
                )}

                {drone.missione && (
                  <p><strong>Missione:</strong> {missioneLabel(drone.missione as MissioneDrone)}</p>
                )}

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {drone.autonomiaMinuti && (
                    <p><strong>Autonomia:</strong> {drone.autonomiaMinuti} min</p>
                  )}
                  {(drone as any).autonomiaResidua !== undefined && (
                    <p><strong>Residua:</strong> {(drone as any).autonomiaResidua} min</p>
                  )}
                  {(drone as any).quotaMetri && (
                    <p><strong>Quota:</strong> {(drone as any).quotaMetri} m</p>
                  )}
                  {(drone as any).raggioKm && (
                    <p><strong>Raggio:</strong> {(drone as any).raggioKm} km</p>
                  )}
                </div>

                {(drone as any).coordsRilevate && (
                  <p style={{ color: '#ef4444', fontWeight: 600 }}>
                    📍 GPS rilevato: {(drone as any).coordsRilevate}
                    {(drone as any).oraRilevamento && ` · ore ${(drone as any).oraRilevamento}`}
                  </p>
                )}

                {(drone as any).condivisioneRealtime && (
                  <p style={{ color: '#22c55e' }}>
                    📡 Streaming SIM attivo → SOUP/DOS
                    {(drone as any).urlStreaming && (
                      <a
                        href={(drone as any).urlStreaming}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: '10px',
                          background: '#16a34a',
                          color: '#fff',
                          padding: '2px 10px',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                        }}
                      >
                        ▶️ Apri Video
                      </a>
                    )}
                  </p>
                )}

                {drone.note && <p><strong>Note:</strong> {drone.note}</p>}
              </div>

              <div className="card-actions">
                {/* Pulsante principale Linea 5B */}
                <button
                  className="btn btn-warning"
                  title="Invia le coordinate GPS rilevate dal drone al form COAU"
                  onClick={() => handleInviaACOAU(drone)}
                  style={{
                    background: (drone as any).coordsRilevate ? '#dc2626' : '#64748b',
                    color: '#fff',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '5px',
                    cursor: (drone as any).coordsRilevate ? 'pointer' : 'not-allowed',
                  }}
                >
                  📍 GPS → COAU
                </button>
                <button className="btn-edit" onClick={() => handleEdit(drone)}>✏️ Modifica</button>
                <button className="btn-delete" onClick={() => handleDelete(drone.id)}>🗑️ Elimina</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modali selezione */}
      <SelectModal
        isOpen={showModelloModal}
        onClose={() => setShowModelloModal(false)}
        title="Seleziona Modello Drone"
        options={MODELLI_DRONE_OPTIONS}
        selectedValue={formData.modello}
        onSelect={handleModelloSelect}
      />
      <SelectModal
        isOpen={showPosizioneModal}
        onClose={() => setShowPosizioneModal(false)}
        title="Seleziona Posizione Operativa"
        options={POSIZIONI_OPERATIVE}
        selectedValue={formData.posizioneOperativa}
        onSelect={(value) => setFormData((p) => ({ ...p, posizioneOperativa: value }))}
      />

      <SelectModal
        isOpen={showMissioneModal}
        onClose={() => setShowMissioneModal(false)}
        title="Seleziona Missione Operativa"
        options={MISSIONI.map((m) => m.label)}
        selectedValue={formData.missione ? missioneLabel(formData.missione) : ''}
        onSelect={(label) => {
          const found = MISSIONI.find((m) => m.label === label);
          if (found) setFormData((p) => ({ ...p, missione: found.valore }));
        }}
      />
    </div>
  );
};
